// Settings → Units: the units products are sold in (pcs, kg, box…). Staff can
// add and edit them; deleting needs a manager and only works for unused units.
import type { RowOf } from '@ims/data'
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Field,
  Input,
  TD,
  TH,
  Table,
  useToast,
  SkeletonTable,
} from '@ims/ui'
import { Plus } from '@ims/ui/icons'
import { useState } from 'react'
import { useLookups } from '../components.tsx'
import { errorMessage, useLive } from '../hooks.ts'
import { deleteOp, insertOp, newUnit, updateOp } from '../lib/rows.ts'
import { findUnit, missingPresets, presetFor } from '../lib/units.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'

type UnitRow = RowOf<'units'>

function useUnitUsage() {
  return (
    useLive(
      async (repo) => {
        const counts = new Map<string, number>()
        for (const p of (await repo.list('products', { limit: 1_000_000 })).rows) {
          if (p.unit_id) counts.set(p.unit_id, (counts.get(p.unit_id) ?? 0) + 1)
        }
        return counts
      },
      [],
      ['products'],
    ).data ?? new Map<string, number>()
  )
}

/** Commits one change with a toast; returns whether it worked. */
function useCommit() {
  const repo = useRepo()
  const toast = useToast()
  const [busy, setBusy] = useState(false)
  const commit = async (ops: Parameters<typeof repo.commit>[0], message: string) => {
    setBusy(true)
    try {
      await repo.commit(ops)
      toast.success(message)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  return { commit, busy }
}

/** A clash with another unit's symbol or name, as a message; null when free. */
function clash(units: UnitRow[], name: string, symbol: string, self?: string): string | null {
  const other = units.find(
    (u) =>
      u.id !== self &&
      [u.symbol, u.name].some((v) =>
        [symbol, name].some((x) => x.trim() && v.trim().toLowerCase() === x.trim().toLowerCase()),
      ),
  )
  return other ? `“${other.name} (${other.symbol})” already exists` : null
}

export function UnitSettings() {
  const lookups = useLookups()
  const usage = useUnitUsage()
  const canEdit = useCan('staff')
  const canDelete = useCan('manager')
  const { current } = useWorkspace()
  const { commit, busy } = useCommit()
  const [editing, setEditing] = useState<string | null>(null)
  const [removing, setRemoving] = useState<UnitRow | null>(null)
  const units = lookups.units
  const suggestions = missingPresets(units).slice(0, 10)

  const add = (fields: { name: string; symbol: string; allows_decimal: boolean }) =>
    current
      ? commit(
          [insertOp('units', newUnit({ tenantId: current.tenant_id }, fields))],
          `Added ${fields.name}`,
        )
      : Promise.resolve(false)

  return (
    <div className="space-y-4">
      <Card className="p-0">
        {!lookups.loaded ? (
          <SkeletonTable rows={4} cols={3} />
        ) : (
          <Table>
            <thead>
              <tr>
                <TH>Name</TH>
                <TH>Symbol</TH>
                <TH>Decimals</TH>
                <TH align="right">Products</TH>
                <TH />
              </tr>
            </thead>
            <tbody>
              {units.map((u) =>
                editing === u.id ? (
                  <EditRow
                    key={u.id}
                    unit={u}
                    units={units}
                    used={usage.get(u.id) ?? 0}
                    busy={busy}
                    onCancel={() => setEditing(null)}
                    onSave={async (changes) => {
                      if (await commit([updateOp('units', u, changes)], 'Unit saved')) {
                        setEditing(null)
                      }
                    }}
                  />
                ) : (
                  <tr key={u.id}>
                    <TD className="font-medium">{u.name}</TD>
                    <TD>{u.symbol}</TD>
                    <TD>
                      {u.allows_decimal ? <Badge tone="primary">Yes (e.g. 1.5)</Badge> : 'No'}
                    </TD>
                    <TD align="right">{usage.get(u.id) ?? 0}</TD>
                    <TD align="right" className="whitespace-nowrap">
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => setEditing(u.id)}>
                          Edit
                        </Button>
                      )}
                      {canDelete && !usage.get(u.id) && (
                        <Button size="sm" variant="ghost" onClick={() => setRemoving(u)}>
                          Delete
                        </Button>
                      )}
                    </TD>
                  </tr>
                ),
              )}
            </tbody>
          </Table>
        )}
        {canEdit && <AddUnit units={units} busy={busy} onAdd={add} />}
      </Card>

      {canEdit && suggestions.length > 0 && (
        <Card>
          <p className="mb-2 text-sm font-semibold">Common units</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((p) => (
              <Button
                key={p.symbol}
                size="sm"
                variant="secondary"
                icon={<Plus className="size-3.5" />}
                disabled={busy}
                onClick={() =>
                  void add({ name: p.name, symbol: p.symbol, allows_decimal: p.allowsDecimal })
                }
              >
                {p.name}
              </Button>
            ))}
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Delete unit"
        message={removing ? `Delete “${removing.name} (${removing.symbol})”?` : undefined}
        confirmLabel="Delete"
        danger
        busy={busy}
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing && (await commit([deleteOp('units', removing.id)], 'Unit deleted'))) {
            setRemoving(null)
          }
        }}
      />
    </div>
  )
}

function EditRow({
  unit,
  units,
  used,
  busy,
  onSave,
  onCancel,
}: {
  unit: UnitRow
  units: UnitRow[]
  used: number
  busy: boolean
  onSave: (changes: Partial<UnitRow>) => void
  onCancel: () => void
}) {
  const [name, setName] = useState(unit.name)
  const [symbol, setSymbol] = useState(unit.symbol)
  const [decimal, setDecimal] = useState(unit.allows_decimal)
  const problem = clash(units, name, symbol, unit.id)
  return (
    <tr>
      <TD>
        <Input aria-label="Unit name" value={name} onChange={(e) => setName(e.target.value)} />
      </TD>
      <TD>
        <Input
          aria-label="Unit symbol"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          className="w-24"
        />
      </TD>
      <TD>
        <Checkbox
          label="Allows decimals"
          checked={decimal}
          onChange={(e) => setDecimal(e.target.checked)}
        />
        {used > 0 && unit.allows_decimal && !decimal && (
          <p className="mt-1 text-xs text-warning">Stock already in decimals stays as it is.</p>
        )}
        {problem && <p className="mt-1 text-xs text-danger">{problem}</p>}
      </TD>
      <TD />
      <TD align="right" className="whitespace-nowrap">
        <Button
          size="sm"
          loading={busy}
          disabled={!name.trim() || !symbol.trim() || !!problem}
          onClick={() =>
            onSave({ name: name.trim(), symbol: symbol.trim(), allows_decimal: decimal })
          }
        >
          Save
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </TD>
    </tr>
  )
}

function AddUnit({
  units,
  busy,
  onAdd,
}: {
  units: UnitRow[]
  busy: boolean
  onAdd: (fields: { name: string; symbol: string; allows_decimal: boolean }) => Promise<boolean>
}) {
  const [name, setName] = useState('')
  const [symbol, setSymbol] = useState('')
  const [decimal, setDecimal] = useState(false)
  const problem = clash(units, name, symbol)
  // Typing a known symbol ("dz", "ltr") fills in the rest.
  const fillFromPreset = () => {
    const preset = presetFor(symbol) ?? presetFor(name)
    if (!preset || findUnit(units, preset.symbol)) return
    if (!name.trim()) setName(preset.name)
    setDecimal(preset.allowsDecimal)
  }
  return (
    <div className="flex flex-wrap items-end gap-3 border-t border-border p-4">
      <Field label="Symbol" className="w-28">
        {(id) => (
          <Input
            id={id}
            value={symbol}
            placeholder="box"
            onChange={(e) => setSymbol(e.target.value)}
            onBlur={fillFromPreset}
          />
        )}
      </Field>
      <Field label="Unit name" className="min-w-40 flex-1">
        {(id) => (
          <Input
            id={id}
            value={name}
            placeholder="Box"
            onChange={(e) => setName(e.target.value)}
            onBlur={fillFromPreset}
          />
        )}
      </Field>
      <div className="pb-2">
        <Checkbox
          label="Allows decimals (kg, litre)"
          checked={decimal}
          onChange={(e) => setDecimal(e.target.checked)}
        />
      </div>
      <Button
        icon={<Plus className="size-4" />}
        loading={busy}
        disabled={!name.trim() || !symbol.trim() || !!problem}
        onClick={() =>
          void onAdd({ name: name.trim(), symbol: symbol.trim(), allows_decimal: decimal }).then(
            (ok) => ok && (setName(''), setSymbol(''), setDecimal(false)),
          )
        }
      >
        Add unit
      </Button>
      {problem && <p className="w-full text-xs text-danger">{problem}</p>}
    </div>
  )
}
